import React from 'react';
import { getChatDisplayName, getChatAvatar, getAvatarInitials } from '../../utils/chatUtils';

export interface UserInfoProps {
  user?: {
    profile?: {
      displayName?: string;
      nickname?: string;
      firstName?: string;
      lastName?: string;
      avatar?: string;
    };
    email?: string;
    reputation?: {
      level?: string;
      score?: number;
    };
    kyc?: {
      status?: string;
      citizenStatus?: boolean;
    };
    badges?: Array<{
      badgeId?: string;
      name: string;
      description?: string;
      awardedAt?: string;
    }>;
  } | string | null;
  size?: 'sm' | 'md' | 'lg';
  showBadges?: boolean;
  showReputation?: boolean;
  className?: string;
  avatarOnly?: boolean;
}

const UserInfo: React.FC<UserInfoProps> = ({
  user,
  size = 'md',
  showBadges = true,
  showReputation = true,
  className = '',
  avatarOnly = false,
}) => {
  if (!user || typeof user === 'string') {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <div className="w-8 h-8 rounded-full overflow-hidden bg-[var(--color-neutral-300)] flex items-center justify-center text-white text-xs font-semibold">
          <span>U</span>
        </div>
        {!avatarOnly && <span className="text-[var(--color-default-font)]">Anonymous</span>}
      </div>
    );
  }

  const displayName = getChatDisplayName(user);
  const avatarUrl = getChatAvatar(user);
  const avatarInitials = getAvatarInitials(displayName);
  const hasAvatar = user.profile?.avatar;

  // Determine reputation level
  // Default is "registered", becomes "citizen" (or higher) when KYC is approved
  const isCitizen = user.kyc?.citizenStatus === true || user.kyc?.status === 'approved';
  const reputationLevel = isCitizen
    ? (user.reputation?.level || 'citizen')
    : 'registered';

  // Size configurations
  const sizeConfig = {
    sm: {
      avatar: 'w-6 h-6',
      text: 'text-xs',
      badge: 'text-[10px] px-1.5 py-0.5',
      badgeIcon: 'w-3 h-3',
    },
    md: {
      avatar: 'w-8 h-8',
      text: 'text-sm',
      badge: 'text-xs px-2 py-1',
      badgeIcon: 'w-4 h-4',
    },
    lg: {
      avatar: 'w-10 h-10',
      text: 'text-base',
      badge: 'text-sm px-2.5 py-1',
      badgeIcon: 'w-5 h-5',
    },
  };

  const config = sizeConfig[size];

  // Reputation level colors and labels
  const reputationConfig: Record<string, { label: string; color: string; bgColor: string }> = {
    registered: {
      label: 'Registered',
      color: 'text-[var(--color-neutral-700)]',
      bgColor: 'bg-[var(--color-neutral-100)]',
    },
    citizen: {
      label: 'Citizen',
      color: 'text-[var(--color-brand-700)]',
      bgColor: 'bg-[var(--color-brand-100)]',
    },
    advocate: {
      label: 'Advocate',
      color: 'text-[var(--color-success-700)]',
      bgColor: 'bg-[var(--color-success-100)]',
    },
    champion: {
      label: 'Champion',
      color: 'text-[var(--color-warning-700)]',
      bgColor: 'bg-[var(--color-warning-100)]',
    },
    leader: {
      label: 'Leader',
      color: 'text-[var(--color-error-700)]',
      bgColor: 'bg-[var(--color-error-100)]',
    },
  };

  const repConfig = reputationConfig[reputationLevel] || reputationConfig.registered;

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {/* Avatar */}
      <div className={`${config.avatar} rounded-full overflow-hidden bg-[var(--color-brand-primary)] flex items-center justify-center text-white ${size === 'sm' ? 'text-xs' : size === 'md' ? 'text-sm' : 'text-base'} font-semibold flex-shrink-0`}>
        {hasAvatar ? (
          <img
            src={avatarUrl}
            alt={displayName}
            className="w-full h-full object-cover"
            onError={(e) => {
              // Fallback to initials if image fails to load
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              const parent = target.parentElement;
              if (parent) {
                parent.textContent = avatarInitials;
              }
            }}
          />
        ) : (
          <span>{avatarInitials}</span>
        )}
      </div>

      {!avatarOnly && (
        <div className="flex items-center gap-2 flex-wrap">
          {/* Name */}
          <span className={`font-medium text-[var(--color-default-font)] ${config.text}`}>
            {displayName}
          </span>

          {/* Reputation Level Badge */}
          {showReputation && (
            <span className={`${config.badge} ${repConfig.bgColor} ${repConfig.color} rounded-full font-medium flex items-center gap-1`}>
              <svg className={config.badgeIcon} fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              {repConfig.label}
            </span>
          )}

          {/* User Badges */}
          {showBadges && user.badges && user.badges.length > 0 && (
            <div className="flex items-center gap-1 flex-wrap">
              {user.badges.slice(0, 3).map((badge, index) => (
                <span
                  key={badge.badgeId || index}
                  className={`${config.badge} bg-[var(--color-warning-100)] text-[var(--color-warning-700)] rounded-full font-medium`}
                  title={badge.description || badge.name}
                >
                  🏆 {badge.name}
                </span>
              ))}
              {user.badges.length > 3 && (
                <span className={`${config.badge} bg-[var(--color-neutral-100)] text-[var(--color-neutral-600)] rounded-full`}>
                  +{user.badges.length - 3}
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UserInfo;

